import React, { useEffect, useState } from 'react';
// @ts-ignore
import moment, { Moment } from 'moment';
import Calendar from './Calendar';
import CalendarPatientEventModal from './CalendarPatientEventModal';
import apiClient, { remove } from '../../Services/ApiClient';
import { workplaceWorkingHours } from '../../Modules/Workplace/Services/workplace-working-hours.service';
import MultiSelect from '../Base/MultiSelect/MultiSelect';
import { useEffectAsync } from '../../helpers';
import { WORKPLACES } from '../../Modules/Workplace/Graphql/workplaces.graphql';
// @ts-ignore
import styles from './AllWorkplacesCalendar.module.scss';
import { useAppContext } from '../../Services/AppContext';
import CalendarContextMenu from './CalendarContextMenu';
import { getMomentDate } from '../../Utils/dates.util';
import useServiceSchedules from '../../Modules/Workplace/Hooks/useServiceSchedules';
import {
	ServiceScheduleType,
	useLazyWorkplaceEventsQuery,
} from '../../graphql/graphql';

interface AllWorkplacesCalendarProps {
	patientId?: string;
}

export default function AllWorkplacesCalendar(
	props: AllWorkplacesCalendarProps,
) {
	const { patientId } = props;
	const { translation, workplaceId } = useAppContext();
	const [getWorkplaceEvents] = useLazyWorkplaceEventsQuery();
	const { getWorkplaceSchedules } = useServiceSchedules();
	const [detailOpened, setDetailOpened] = useState(false);
	const [selectedDate, setSelectedDate] = useState(null);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [dateRange, setDateRange] = useState({ start: null, end: null });
	const [events, setEvents] = useState([]);
	const [workplacesIds, setWorkplacesIds] = useState([]);
	const [selectedWorkplaceId, setSelectedWorkplaceId] = useState(null);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [resources, setResources] = useState([]);
	const [workplaces, setWorkplaces] = useState([]);
	const [contextMenuProps, setContextMenuProps] = useState({
		isOpen: false,
	});

	// @ts-ignore
	useEffectAsync(async () => {
		await handleLoadResources();
	}, []);

	useEffect(() => {
		handleSetResources();
	}, [workplacesIds]);

	useEffect(() => {
		handleSetResources();
	}, [workplaces]);

	// @ts-ignore
	useEffectAsync(async () => {
		setLoadingEvents(true);
		await handleLoadData();
	}, [dateRange, resources]);

	useEffect(() => {
		setLoadingEvents(false);
	}, [events]);

	const handleSetResources = () => {
		const tWorkplacesIds = workplacesIds.length ? workplacesIds : [workplaceId];
		setResources(workplaces.filter(w => tWorkplacesIds.indexOf(w.id) !== -1));
	};

	const handlePickDate = (date, resourceId = null) => {
		setSelectedDate(date);
		setSelectedWorkplaceId(resourceId);
		setDetailOpened(true);
	};

	const handleEventClick = event => {
		setSelectedEvent(event);
		setContextMenuProps({
			isOpen: true,
			clientX: event.jsEvent.clientX,
			clientY: event.jsEvent.clientY,
		});
	};

	const handleEditEvent = () => {
		setSelectedWorkplaceId(selectedEvent?.event?.extendedProps.workplaceId);
		setDetailOpened(true);
		setContextMenuProps({ isOpen: false });
	};

	const handleDateChange = (start: Moment, end?: Moment) => {
		setDateRange({ start, end });
	};

	// @ts-ignore
	const handleClose = async () => {
		setSelectedEvent(null);
		setDetailOpened(false);
		await handleLoadData();
	};

	// @ts-ignore
	const handleRemoveEvent = async function (event) {
		await apiClient.delete('/events/cancel/' + event.event.id);
		setContextMenuProps({ isOpen: false });
		handleLoadData();
	};

	const handleLoadData = () => {
		if (!dateRange.start || !resources.length) {
			setEvents([]);
			return;
		}

		// @ts-ignore
		const a = async () => {
			const tempEvents = [];

			const variables = {
				start: dateRange.start.format(),
				end: (dateRange?.end || dateRange.start).format(),
				types: ['DEFAULT', 'RESERVATION'],
				workplaceIds: resources.map(r => r.id),
			};

			const workplaceEvents = await getWorkplaceEvents(variables);

			for (const event of workplaceEvents?.data?.workplaceEvents?.data || []) {
				let isChild = false;
				let patientsName;
				let patientPhone;
				let patientBirthNumber;
				let patientAge;
				let patientId;
				let doctor: { id?: string; name?: string } = {};
				event?.eventMembers?.forEach((user, index) => {
					if (user?.user?.isPatient) {
						patientId = user?.user?.id;
						patientsName = user.user?.profile?.fullName;
						patientAge = user.user?.profile?.age;
						patientBirthNumber = user?.user?.profile?.birthNumber;
						patientPhone = user?.user?.formattedPhoneNumber;
					}
					if (user?.user?.isChild) {
						patientId = user?.user?.id;
						patientsName = user?.user?.profile?.fullName;
						patientAge = user?.user?.profile?.age;
						patientBirthNumber = user?.user?.profile?.birthNumber;
						patientPhone = user?.user?.formattedPhoneNumber;
						isChild = true;
					}
					if (user?.user?.isDoctor) {
						if (!doctor.name) {
							doctor.name = user.user?.profile?.fullName;
							doctor.id = user.user?.id;
						}
					}
				});
				tempEvents.push({
					id: event.id,
					title: event.title,
					doctor,
					patient: {
						id: patientId ? patientId : '',
						name: patientsName ? patientsName : '',
						age: patientAge ? patientAge : '',
						birthNumber: patientBirthNumber ? patientBirthNumber : '',
						phone: patientPhone ? patientPhone : '',
						isChild,
					},
					workplace: event.calendar.workplace.doctorDisplayName ?? null,
					workplaceId: event.calendar.workplaceId ?? null,
					start: moment(event.dateFrom).format(),
					end: moment(event.dateTo).format(),
					backgroundColor: event.layout.backgroundColor,
					borderColor: event.layout.backgroundColor,
					textColor: event.layout.fontColor,
					resourceId: event.calendar.workplaceId ?? null,
					note: event.description,
					meta: event.meta,
				});
			}

			const tempEvents2 = await getWorkplaceSchedules({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end || dateRange.start),
				workplaceIds: resources.map(r => r.id),
				type: ServiceScheduleType.Shift,
				allDay: true,
			});
			tempEvents2.map(t => tempEvents.push(t));

			const tempEvents3 = await getWorkplaceSchedules({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end || dateRange.start),
				workplaceIds: resources.map(r => r.id),
				type: ServiceScheduleType.ShiftExtra,
				allDay: true,
			});
			tempEvents3.map(t => tempEvents.push(t));
			setEvents(tempEvents);
		};

		a();
	};

	const handleEventDrop = event => {
		let workplaceId;
		for (const resource of event.getResources()) {
			workplaceId = resource.id;
		}

		// @ts-ignore
		const a = async () => {
			await apiClient.patch(`/event/${event.id}`, {
				dateFrom: moment(event.start).format(),
				dateTo: moment(event.end).format(),
				propIgnoreFreeTimeCheck: true,
				workplaceId,
			});
		};

		a();
	};

	// @ts-ignore
	const handleLoadResources = async () => {
		const tempWorkplaces = [];

		const { meta, data } = await apiClient.graphql({
			query: WORKPLACES,
			variables: {
				type: 'normal',
			},
			fetchPolicy: 'network-only',
		});

		for (const workplace of data.workplaces.data || []) {
			const item = {
				id: workplace.id,
				value: workplace.id,
				title: workplace.doctorDisplayName,
				label: workplace.doctorDisplayName,
				businessHours: [],
			};

			const businessHours = await workplaceWorkingHours({
				workplaceId: workplace.id,
			});
			item.businessHours = businessHours;

			tempWorkplaces.push(item);
		}

		setWorkplaces(tempWorkplaces);
	};

	// @ts-ignore
	const handleSelectWorkplaces = async values => {
		const tempWorkplacesIds = [];

		for (const workplace of values || []) {
			tempWorkplacesIds.push(workplace.value);
		}
		setWorkplacesIds(tempWorkplacesIds);
	};

	const handleCreateNewEvent = (date: Moment) => {
		setSelectedEvent(null);
		handlePickDate(getMomentDate(date));
	};

	const handleAfterMarkDone = () => {
		setSelectedEvent(null);
		setContextMenuProps({ isOpen: false });
		handleLoadData();
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.selectWrapper}>
				<MultiSelect
					options={workplaces}
					value={workplacesIds}
					onChangeExtra={handleSelectWorkplaces}
					isSearchable={true}
					placeholder={translation['base.chooseWorkplace']}
				/>
			</div>
			<div className={styles.calendarWrapper}>
				<Calendar
					events={events}
					onTimeClick={handlePickDate}
					onEventClick={handleEventClick}
					initialView={'resourceTimeGridDay'}
					onDateChange={handleDateChange}
					allDaySlot={true}
					editable={true}
					onEventDrop={handleEventDrop}
					onEventResize={handleEventDrop}
					resources={resources}
					loadingEvents={loadingEvents}
					headerToolbar={{
						left: 'prev,next,today',
						center: 'title',
						right: 'resourceTimeGridDay',
					}}
					views={{
						day: {
							titleFormat: {
								year: 'numeric',
								month: 'long',
								day: 'numeric',
								weekday: 'long',
							},
						},
					}}
				/>
				{selectedEvent && (
					<CalendarContextMenu
						contextParams={contextMenuProps}
						event={selectedEvent}
						onEdit={() => handleEditEvent()}
						onRemove={handleRemoveEvent}
						onCreateNewEvent={handleCreateNewEvent}
						onContextClose={() => {
							setContextMenuProps({ isOpen: false });
						}}
						onMarkEventDone={handleAfterMarkDone}
					/>
				)}
				<CalendarPatientEventModal
					patientId={patientId}
					opened={detailOpened}
					onClose={handleClose}
					selectedDate={selectedDate}
					defaultData={selectedEvent}
					workplaceId={selectedWorkplaceId}
				/>
			</div>
		</div>
	);
}
